import {
  ICPEmployeeCountDistribution,
  ICPGeographyDistribution,
  ICPIndustryDistribution,
  ICPRevenueDistribution,
} from "../../apis/companySearch/icpDistribution";

/**
 * Generic interface for win/loss data items
 * All win/loss distribution types share these common properties
 */
export interface WinLossDataItem {
  countWon: number;
  countLost: number;
}

interface ICPDistributionData {
  industryDistribution: ICPIndustryDistribution[];
  icpIndustryDistribution: ICPIndustryDistribution[];
  geographyDistribution: ICPGeographyDistribution[];
  icpGeographyDistribution: ICPGeographyDistribution[];
  revenueDistribution: ICPRevenueDistribution[];
  icpRevenueDistribution: ICPRevenueDistribution[];
  employeeCountDistribution: ICPEmployeeCountDistribution[];
  icpEmployeeCountDistribution: ICPEmployeeCountDistribution[];
}

export const tabItems = [
  {
    id: "definition-tab",
    label: "Definition",
  },
  {
    id: "analysis-tab",
    label: "Analysis",
  },
];

/**
 * Downloads a CSV template for customer list upload
 */
/* update to match whatever item we want to download */
export const downloadCustomerTemplate = () => {
  const headers = [
    "HG Company ID",
    "DUNS",
    "URL",
    "Company Name",
    "Country",
    "CRM ID",
    "Win_Loss",
  ];

  const sampleData = [
    [
      "2CAB0AE97A39BF70F69E1B90E58C2CF3",
      "985104790",
      "libertymutual.com",
      "Liberty Mutual",
      "United States",
      "0010h00001Y6dpW",
      "true",
    ],
    [
      "22F251E4FC257F782AE9547082FD284A",
      "836136721",
      "chumashcasino.com",
      "Chumash Casino Resort",
      "United States",
      "0010h00001Y7MBX",
      "false",
    ],
    [
      "10680B77E5228023333426447E83371F",
      "357072347",
      "teracom.se",
      "Teracom AB",
      "Sweden",
      "0010h00001Y7T5Y",
      "true",
    ],
  ];

  // Create CSV content
  const csvContent = [
    headers.join(","),
    ...sampleData.map((row) => row.join(",")),
  ].join("\n");

  // Create and download file
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "customer_list_template.csv");
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Check if win/loss data contains meaningful information
 * Returns true if there are any items with actual wins or losses
 *
 * @param data Array of win/loss data items
 * @returns boolean indicating if there's meaningful win/loss data
 */
export const hasWinLossData = <T extends WinLossDataItem>(
  data: T[]
): boolean => {
  return data.some((item) => item.countWon > 0 || item.countLost > 0);
};

export function isAnalysisResultsSame(distributionData: ICPDistributionData) {
  const isIndustryDistributionSame = (() => {
    const current = distributionData.industryDistribution;
    const icp = distributionData.icpIndustryDistribution;
    if (current.length === 0 && icp.length === 0) return true;
    if (current.length !== icp.length) return false;
    return current.every(
      (item, index) => item.industryId === icp[index].industryId
    );
  })();

  const isGeographyDistributionSame = (() => {
    const current = distributionData.geographyDistribution;
    const icp = distributionData.icpGeographyDistribution;
    if (current.length === 0 && icp.length === 0) return true;
    if (current.length !== icp.length) return false;
    return current.every(
      (item, index) => item.countryCode === icp[index].countryCode
    );
  })();

  const isRevenueDistributionSame = (() => {
    const current = distributionData.revenueDistribution;
    const icp = distributionData.icpRevenueDistribution;
    if (current.length === 0 && icp.length === 0) return true;
    if (current.length !== icp.length) return false;
    return current.every(
      (item, index) =>
        item.revenueRangeMin === icp[index].revenueRangeMin &&
        item.revenueRangeMax === icp[index].revenueRangeMax
    );
  })();

  const isEmployeeCountDistributionSame = (() => {
    const current = distributionData.employeeCountDistribution;
    const icp = distributionData.icpEmployeeCountDistribution;
    if (current.length === 0 && icp.length === 0) return true;
    if (current.length !== icp.length) return false;
    return current.every(
      (item, index) =>
        item.employeesRangeMin === icp[index].employeesRangeMin &&
        item.employeesRangeMax === icp[index].employeesRangeMax
    );
  })();

  return (
    isIndustryDistributionSame &&
    isGeographyDistributionSame &&
    isRevenueDistributionSame &&
    isEmployeeCountDistributionSame
  );
}
